import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clientFetch } from "../utils/clientFetch";

/**
 * APIアクセス監視ダッシュボード (#471 / 管理画面 Phase E) のデータ取得フック。
 *
 * バックエンド制約 (検証済み): 統計は **APIキー単位** のみ。
 *   GET ${API_BASE}/external-api-keys/:id/stats?from&to&granularity(hour|day)
 *   → data:{ series: StatsRow[] }。同一 bucket に status_class ごとの複数行が来るため、
 *     グラフ描画時はクライアントで bucket 単位に集計する (aggregateByBucket)。
 * 企業横断・5分粒度・異常検知・sample_id 追跡・生ログ CSV は未実装 (#457) のため提供しない。
 */

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;

export type StatsGranularity = "hour" | "day";
export type StatusClass = "2xx" | "3xx" | "4xx" | "5xx" | "other";

export const STATUS_CLASSES: StatusClass[] = ["2xx", "3xx", "4xx", "5xx", "other"];

/** stats API の 1 行 (bucket × status_class 単位の集計値)。 */
export type StatsRow = {
  bucket: string; // ISO 日時
  status_class: StatusClass;
  call_count: number;
  rate_limited_count: number;
  avg_response_ms: number | null;
  total_response_bytes: number | null;
};

/** 企業の active キー Select 用。 */
export type StatsKeyOption = {
  id: number;
  key_prefix: string;
  name: string;
};

/** bucket 単位に集計した 1 点 (グラフ描画用)。status_class 別件数も保持する。 */
export type BucketAggregate = {
  bucket: string;
  total: number; // 全 status_class の call_count 合計
  rateLimited: number; // rate_limited_count 合計
  errorRate: number; // (4xx+5xx)/total * 100 (total=0 のとき 0)
  // status_class 別 call_count (積み上げグラフ用、全クラスのキーを必ず持つ)
  "2xx": number;
  "3xx": number;
  "4xx": number;
  "5xx": number;
  other: number;
};

export type StatsFetchParams = { from?: string; to?: string; granularity: StatsGranularity };

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.message || `エラーが発生しました (${res.status})`;
  } catch {
    return `エラーが発生しました (${res.status})`;
  }
}

/**
 * series を bucket 単位に集計する純関数 (グラフ描画用、テスト対象)。
 * - 同一 bucket の全 status_class 行を合算する。
 * - total=0 の bucket は errorRate=0 (NaN にしない)。
 * - status_class が欠けた bucket でも全クラスのキーを 0 で埋める (積み上げグラフのキー整合)。
 * - bucket 昇順 (時系列) でソートして返す (API が順不同でも安定させる)。
 */
export function aggregateByBucket(series: StatsRow[]): BucketAggregate[] {
  const map = new Map<string, BucketAggregate>();
  for (const row of series) {
    let agg = map.get(row.bucket);
    if (!agg) {
      agg = {
        bucket: row.bucket,
        total: 0,
        rateLimited: 0,
        errorRate: 0,
        "2xx": 0,
        "3xx": 0,
        "4xx": 0,
        "5xx": 0,
        other: 0,
      };
      map.set(row.bucket, agg);
    }
    agg.total += row.call_count;
    agg.rateLimited += row.rate_limited_count;
    agg[row.status_class] += row.call_count;
  }
  const result = Array.from(map.values());
  for (const agg of result) {
    const errors = agg["4xx"] + agg["5xx"];
    agg.errorRate = agg.total > 0 ? (errors / agg.total) * 100 : 0;
  }
  result.sort((a, b) => (a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : 0));
  return result;
}

export function useApiAccessStatsContext() {
  const [keyOptions, setKeyOptions] = useState<StatsKeyOption[]>([]);
  const [series, setSeries] = useState<StatsRow[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** 企業の active APIキー一覧を取得する (Select 用)。 */
  const fetchKeys = useCallback(async (companyId: number) => {
    setKeysLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams({
        companyId: String(companyId),
        status: "active",
        page: "1",
        limit: "100",
      });
      const res = await clientFetch(`${API_BASE}/external-api-keys?${q.toString()}`, { method: "GET" });
      if (!res.ok) {
        setKeyOptions([]);
        setError(await parseError(res));
        return;
      }
      const body = await res.json();
      const items: StatsKeyOption[] = Array.isArray(body?.data)
        ? body.data.map((k: { id: number; key_prefix: string; name: string }) => ({
            id: k.id,
            key_prefix: k.key_prefix,
            name: k.name,
          }))
        : [];
      setKeyOptions(items);
    } finally {
      setKeysLoading(false);
    }
  }, []);

  /** APIキー単位の統計 series を取得する。 */
  const fetchStats = useCallback(async (apiKeyId: number, params: StatsFetchParams) => {
    setStatsLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams({ granularity: params.granularity });
      if (params.from) q.set("from", params.from);
      if (params.to) q.set("to", params.to);
      const res = await clientFetch(`${API_BASE}/external-api-keys/${apiKeyId}/stats?${q.toString()}`, {
        method: "GET",
      });
      if (!res.ok) {
        setSeries([]);
        setError(await parseError(res));
        return;
      }
      const body = await res.json();
      setSeries(Array.isArray(body?.data?.series) ? (body.data.series as StatsRow[]) : []);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /** キー選択リセット時などに series をクリアする。 */
  const clearStats = useCallback(() => setSeries([]), []);

  return useMemo(
    () => ({
      keyOptions,
      series,
      keysLoading,
      statsLoading,
      error,
      fetchKeys,
      fetchStats,
      clearStats,
    }),
    [keyOptions, series, keysLoading, statsLoading, error, fetchKeys, fetchStats, clearStats]
  );
}

type ApiAccessStatsContextValue = ReturnType<typeof useApiAccessStatsContext>;
const ApiAccessStatsContext = createContext<ApiAccessStatsContextValue | null>(null);

export function ApiAccessStatsProvider({ children }: { children: React.ReactNode }) {
  const value = useApiAccessStatsContext();
  return <ApiAccessStatsContext.Provider value={value}>{children}</ApiAccessStatsContext.Provider>;
}

export function useApiAccessStats(): ApiAccessStatsContextValue {
  const ctx = useContext(ApiAccessStatsContext);
  if (!ctx) throw new Error("useApiAccessStats must be used within ApiAccessStatsProvider");
  return ctx;
}
